import { buildLineDiff, type DiffLine } from '../ui/diff';
import type { KbChange, KbChangeKind } from '../../lib/api';

/**
 * The review queue's presentation model — pure, so every card, tile and count reads the same answer.
 *
 * Nothing here changes what a change *is* or what the buttons do; it only decides how a change is
 * described to the person reviewing it. The one idea it adds is the **review type**: why a change
 * needs a person, as opposed to what operation it was. `KbChangeKind` says "CREATE"; the reviewer
 * wants to know "did I ask for this?", "is something being thrown away?", "was the agent unsure?".
 */

/**
 * Why a change is in front of a person. The queue holds three things — conflicting information, edits
 * the agent was unsure of, and sensitive information — plus `fix` for the past changes of a reviewer's
 * own fix run, which used to be flagged for being a fix and can still appear under the other filters.
 */
export type ReviewType = 'conflict' | 'unsure' | 'personal' | 'fix';

export const REVIEW_TYPES: ReviewType[] = ['conflict', 'personal', 'unsure', 'fix'];

/**
 * Tones are literal class strings, never computed — Tailwind scans source text, so a class built at
 * runtime generates no CSS.
 */
export const REVIEW_TYPE_META: Record<ReviewType, { label: string; pill: string; dot: string; why: string }> = {
  conflict: {
    label: 'Conflict',
    // Solid rather than tinted like the others: a conflict is the one reason a reviewer must not skim past.
    pill: 'bg-red-600 text-white ring-red-600 dark:bg-red-500 dark:text-white dark:ring-red-500',
    dot: 'bg-red-600',
    why: 'bg-red-50 text-red-950 border-red-600 dark:bg-red-950/50 dark:text-red-100 dark:border-red-500',
  },
  // Keyed `personal` (stored as PERSONAL_INFO) from when personal information was the only kind; it
  // now covers every guardrail category the agent kept a detail from, the category leading its note.
  personal: {
    label: 'Sensitive',
    pill: 'bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:ring-violet-900',
    dot: 'bg-violet-500',
    why: 'bg-violet-50/70 text-violet-900 border-violet-300 dark:bg-violet-950/30 dark:text-violet-200 dark:border-violet-800',
  },
  unsure: {
    label: 'Unsure',
    pill: 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900',
    dot: 'bg-amber-500',
    why: 'bg-amber-50/70 text-amber-900 border-amber-300 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-800',
  },
  fix: {
    label: 'Your fix',
    pill: 'bg-surface-muted text-ink-secondary ring-border',
    dot: 'bg-ink-faint',
    why: 'bg-surface-muted text-ink-secondary border-border',
  },
};

/** True when the change came from a run a reviewer asked for — a review-comment fix or a sweep fix. */
export function isFixChange(change: KbChange): boolean {
  return Boolean(change.run?.scopePath || change.run?.sweepFindingId);
}

/** The reason a change was flagged, or null when it was applied without review. */
export function reviewTypeOf(change: KbChange): ReviewType | null {
  switch (change.flagReason) {
    case 'CONFLICT':
      return 'conflict';
    case 'PERSONAL_INFO':
      return 'personal';
    case 'UNSURE':
      return 'unsure';
  }
  if (change.reviewStatus === 'NOT_REQUIRED') return null;
  return isFixChange(change) ? 'fix' : 'unsure';
}

/** The one line a reviewer reads first: the agent's note, or what "unsure" means. */
export function reasonLineOf(change: KbChange): string | null {
  if (change.flagDetail) return change.flagDetail;
  const type = reviewTypeOf(change);
  if (type === 'unsure') return "The agent wasn't sure this edit is right.";
  if (type === 'conflict') return 'This contradicts what the page said before.';
  if (type === 'personal') return 'This includes sensitive information.';
  return null;
}

const FLOOR_KIND_WORD: Record<KbChangeKind, string> = {
  CREATE: 'new pages',
  UPDATE: 'edits',
  DELETE: 'removals',
  MERGE: 'merges',
  MOVE: 'moves',
};

/** The first sentence of a passage, whole — the card wraps it rather than cutting it off. */
function firstSentence(text: string): string {
  const trimmed = text.trim().replace(/\s+/g, ' ');
  const end = trimmed.search(/[.!?](\s|$)/);
  return end === -1 ? trimmed : trimmed.slice(0, end + 1);
}

/**
 * One short line under the reason: why the agent may have been unsure, or what it was doing when it
 * met the conflict. The agent writes an `Unsure: …` sentence into its reasoning when its confidence is
 * low, and that is the answer when it is there. Otherwise the line is built from the row: the
 * confidence against the floor it missed (stamped at commit — stated only when it actually fell
 * under, since backfilled rows can carry a floor they cleared), then the first sentence of the
 * agent's own reasoning. Never repeats the reason line above it.
 */
export function whyLineOf(change: KbChange): string | null {
  const type = reviewTypeOf(change);
  if (!type || type === 'fix') return null;
  const reasoning = change.reasoning?.trim() ?? '';

  const stated = reasoning.match(/\bUnsure:\s*([^\n]+)/i)?.[1];
  if (stated) return firstSentence(stated.charAt(0).toUpperCase() + stated.slice(1));

  const parts: string[] = [];
  if (type === 'unsure' && change.reviewFloor != null && change.confidence < change.reviewFloor) {
    const kind = FLOOR_KIND_WORD[change.reviewFloorKind ?? change.kind];
    parts.push(`${Math.round(change.confidence * 100)}% confident, under the ${Math.round(change.reviewFloor * 100)}% bar for ${kind}`);
  }
  const lead = reasoning ? firstSentence(reasoning) : '';
  if (lead && lead !== change.flagDetail?.trim()) parts.push(lead);
  return parts.length ? parts.join(' — ') : null;
}

export function restructureLabel(kind: KbChangeKind): string {
  return kind === 'MERGE' ? 'Merged' : 'Moved';
}

/** One card in the queue: a single change, or every change one fix produced. */
export interface ReviewItem {
  key: string;
  type: ReviewType | null;
  /** The project the card is listed under — its first change's, for a fix spanning projects. */
  projectId: string;
  changes: KbChange[];
}

/**
 * Folds the fix-run changes of one piece of work into one card.
 *
 * A sweep fix that splits a page into eleven dated children is one decision by one person, and
 * eleven cards made it look like eleven things to judge. The group key is the sweep card when there
 * is one (its fix can run in two projects — a trim here, a destination there) and the run otherwise.
 * Ordinary flagged changes are never grouped: each was flagged for its own reason.
 */
export function groupReviewItems(changes: KbChange[]): ReviewItem[] {
  const items: ReviewItem[] = [];
  const byKey = new Map<string, ReviewItem>();

  for (const change of changes) {
    const groupKey = isFixChange(change)
      ? change.run?.sweepFindingId
        ? `finding:${change.run.sweepFindingId}`
        : change.runId
          ? `run:${change.runId}`
          : null
      : null;

    if (!groupKey) {
      items.push({ key: change.id, type: reviewTypeOf(change), projectId: change.projectId, changes: [change] });
      continue;
    }
    const existing = byKey.get(groupKey);
    if (existing) {
      existing.changes.push(change);
      continue;
    }
    const item: ReviewItem = { key: groupKey, type: 'fix', projectId: change.projectId, changes: [change] };
    byKey.set(groupKey, item);
    items.push(item);
  }

  // Inside a group, the order work was done in — which is also the order a reader follows it.
  for (const item of items) {
    if (item.changes.length > 1) item.changes.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }
  return items;
}

export function titleOf(change: KbChange): string {
  return change.afterTitle ?? change.beforeTitle ?? change.node?.title ?? change.path;
}

/** "Sep 21" — the queue is about recent days, so the year and time are noise on a card. */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(date);
}

export function localMidnight(now = new Date()): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

// ─── Sections of a diff ──────────────────────────────────────────────────────

const HEADING = /^(#{1,6})[ \t]+(.+?)[ \t]*#*[ \t]*$/;
const FENCE = /^[ \t]{0,3}(`{3,}|~{3,})/;

export type SectionStatus = 'added' | 'removed' | 'edited' | 'unchanged';

export interface DiffSection {
  key: string;
  /** The heading as the page now says it (the old one, for a removed section); null = the preamble. */
  heading: string | null;
  /** Set when the heading line itself was rewritten. */
  renamedFrom: string | null;
  /** Indices into the whole-body diff — so comments and evidence match against one line list. */
  lineIndexes: number[];
  added: number;
  removed: number;
  status: SectionStatus;
  /** The last line of this section on the page's new side, 0-based — where "Add a line" inserts. */
  lastNewIndex: number | null;
}

export interface SectionedDiff {
  lines: DiffLine[];
  sections: DiffSection[];
  added: number;
  removed: number;
}

function headingText(line: string): string | null {
  const match = HEADING.exec(line);
  return match ? match[2]!.trim() : null;
}

/**
 * The diff of a page, cut where its headings are.
 *
 * One line diff over the whole body (the same `buildLineDiff` the classic view renders, so the two
 * can never disagree about what changed), then split at every heading line on either side. Heading
 * rules follow the knowledge base's own parser — flat sections, any level ends the previous one,
 * headings inside fenced code are not headings — tracked per side, since a fence can open in the old
 * text and not the new. A heading rewritten in place (a removed heading immediately followed by an
 * added one) is one section renamed, not a section removed and another added.
 *
 * Blank lines are rendered but not counted: a paragraph break moving is not something to review.
 */
export function sectionDiff(before: string, after: string): SectionedDiff {
  const lines = buildLineDiff(before, after).filter(
    // Diffing against an empty side yields one phantom blank line on that side.
    (line) => !(line.text === '' && ((line.type === 'remove' && before === '') || (line.type === 'add' && after === ''))),
  );

  const sections: DiffSection[] = [];
  let current: DiffSection | null = null;
  let oldFence: string | null = null;
  let newFence: string | null = null;

  const open = (heading: string | null, key: string): DiffSection => {
    const section: DiffSection = {
      key,
      heading,
      renamedFrom: null,
      lineIndexes: [],
      added: 0,
      removed: 0,
      status: 'unchanged',
      lastNewIndex: null,
    };
    sections.push(section);
    return section;
  };

  const step = (fence: string | null, text: string): { fence: string | null; heading: string | null } => {
    const fenceMatch = FENCE.exec(text);
    if (fenceMatch) {
      const marker = fenceMatch[1]!;
      if (fence === null) return { fence: marker[0]!.repeat(marker.length), heading: null };
      return { fence: marker.startsWith(fence) ? null : fence, heading: null };
    }
    return { fence, heading: fence === null ? headingText(text) : null };
  };

  lines.forEach((line, index) => {
    let heading: string | null = null;
    if (line.type !== 'add') {
      const r = step(oldFence, line.text);
      oldFence = r.fence;
      heading = r.heading;
    }
    if (line.type !== 'remove') {
      const r = step(newFence, line.text);
      newFence = r.fence;
      heading = heading ?? r.heading;
    }

    if (heading !== null) {
      const justRemovedHeading =
        current !== null &&
        current.lineIndexes.length === 1 &&
        lines[current.lineIndexes[0]!]!.type === 'remove' &&
        line.type === 'add';
      if (justRemovedHeading && current) {
        current.renamedFrom = current.heading;
        current.heading = heading;
      } else {
        current = open(heading, `s-${index}`);
      }
    } else if (current === null) {
      current = open(null, 'top');
    }

    current!.lineIndexes.push(index);
    if (line.text.trim() !== '') {
      if (line.type === 'add') current!.added++;
      if (line.type === 'remove') current!.removed++;
    }
    if (line.newLineNo !== null) current!.lastNewIndex = line.newLineNo - 1;
  });

  const kept = sections.filter((s) => s.lineIndexes.some((i) => lines[i]!.text.trim() !== ''));
  for (const s of kept) {
    const substantive = s.lineIndexes.map((i) => lines[i]!).filter((l) => l.text.trim() !== '');
    if (s.added + s.removed === 0) s.status = 'unchanged';
    else if (substantive.every((l) => l.type === 'add')) s.status = 'added';
    else if (substantive.every((l) => l.type === 'remove')) s.status = 'removed';
    else s.status = 'edited';
  }

  return {
    lines,
    sections: kept,
    added: kept.reduce((n, s) => n + s.added, 0),
    removed: kept.reduce((n, s) => n + s.removed, 0),
  };
}

export function sectionLabel(section: DiffSection): string {
  return section.heading ?? 'Opening';
}

/** The one-line account under a section's name — what happened to it, in words. */
export function sectionSubtitle(section: DiffSection): string {
  if (section.renamedFrom) {
    return section.added + section.removed > 2
      ? `Renamed from “${section.renamedFrom}”, and edited.`
      : `Renamed from “${section.renamedFrom}”.`;
  }
  switch (section.status) {
    case 'added':
      return 'New section.';
    case 'removed':
      return 'Removed from this page.';
    case 'unchanged':
      return 'Unchanged.';
    case 'edited': {
      if (section.removed === 0) return `${section.added} ${section.added === 1 ? 'line' : 'lines'} added.`;
      if (section.added === 0) return `${section.removed} ${section.removed === 1 ? 'line' : 'lines'} removed.`;
      return 'Edited.';
    }
  }
}

// ─── Words ───────────────────────────────────────────────────────────────────

/** The card's headline: what happened, as a sentence about the page rather than an operation code. */
export function headlineOf(change: KbChange, diff: SectionedDiff | null): string {
  const title = titleOf(change);
  switch (change.kind) {
    case 'CREATE':
      return `New page: “${title}”`;
    case 'DELETE':
      return change.fromPath ? `“${title}” was folded into another page` : `“${title}” was removed`;
    case 'MOVE':
      return change.beforeTitle && change.afterTitle && change.beforeTitle !== change.afterTitle
        ? `“${change.beforeTitle}” was moved and renamed “${change.afterTitle}”`
        : `“${title}” was moved`;
    case 'MERGE':
      return change.mergedFromPaths.length > 0
        ? `${change.mergedFromPaths.length} ${change.mergedFromPaths.length === 1 ? 'page was' : 'pages were'} merged into “${title}”`
        : `Pages were merged into “${title}”`;
    case 'UPDATE': {
      const touched = diff?.sections.filter((s) => s.status !== 'unchanged') ?? [];
      if (touched.length === 1) return `“${title}” was edited in “${sectionLabel(touched[0]!)}”`;
      if (touched.length > 1) return `“${title}” was edited in ${touched.length} sections`;
      return `“${title}” was edited`;
    }
  }
}

export interface ChangeBullet {
  tone: 'add' | 'remove' | 'edit' | 'move';
  text: string;
}

function listNames(names: string[], max = 3): string {
  const quoted = names.slice(0, max).map((n) => `“${n}”`);
  const rest = names.length - max;
  return rest > 0 ? `${quoted.join(', ')} and ${rest} more` : quoted.join(', ');
}

/**
 * "What changed" — derived from the diff and the row, never written by a model, so it cannot claim
 * something the exact changes below it do not show.
 */
export function whatChangedOf(change: KbChange, diff: SectionedDiff | null): ChangeBullet[] {
  const bullets: ChangeBullet[] = [];

  if (change.kind === 'MOVE') {
    bullets.push({ tone: 'move', text: `Re-filed from ${change.fromPath ?? 'its old place'} to ${change.path}. The text is unchanged.` });
    return bullets;
  }
  if (change.kind === 'DELETE') {
    const count = diff?.sections.length ?? 0;
    bullets.push({
      tone: 'remove',
      text: change.fromPath
        ? `Its content now lives in ${change.fromPath}; this page is gone.`
        : `The whole page is gone${count > 1 ? ` (${count} sections)` : ''}.`,
    });
    return bullets;
  }
  if (change.kind === 'MERGE' && change.mergedFromPaths.length > 0) {
    bullets.push({ tone: 'move', text: `Folded in: ${change.mergedFromPaths.join(', ')}.` });
  }
  if (change.beforeTitle && change.afterTitle && change.beforeTitle !== change.afterTitle) {
    bullets.push({ tone: 'edit', text: `Retitled from “${change.beforeTitle}”.` });
  }
  if (!diff) return bullets;

  const named = (status: SectionStatus) =>
    diff.sections.filter((s) => s.status === status && !s.renamedFrom).map(sectionLabel);

  if (change.kind === 'CREATE') {
    const all = diff.sections.map(sectionLabel).filter((n) => n !== 'Opening');
    bullets.push({
      tone: 'add',
      text:
        all.length > 0
          ? `A new page with ${all.length} ${all.length === 1 ? 'section' : 'sections'}: ${listNames(all)}.`
          : `A new page, ${diff.added} ${diff.added === 1 ? 'line' : 'lines'} long.`,
    });
    return bullets;
  }

  const added = named('added');
  const removed = named('removed');
  const edited = named('edited');
  const renamed = diff.sections.filter((s) => s.renamedFrom);
  if (added.length) bullets.push({ tone: 'add', text: `Added ${listNames(added)}.` });
  if (edited.length) bullets.push({ tone: 'edit', text: `Edited ${listNames(edited)}.` });
  if (renamed.length) {
    bullets.push({
      tone: 'edit',
      text: `Renamed ${renamed.map((s) => `“${s.renamedFrom}” to “${s.heading}”`).slice(0, 2).join(', ')}.`,
    });
  }
  if (removed.length) bullets.push({ tone: 'remove', text: `Removed ${listNames(removed)}.` });
  return bullets;
}

/** What Undo does, per kind — the button is one word, so the tooltip carries the consequence. */
export function undoMeaning(kind: KbChangeKind): string {
  switch (kind) {
    case 'CREATE':
      return 'Remove this new page';
    case 'UPDATE':
      return 'Put the page back the way it was';
    case 'DELETE':
      return 'Bring the page back';
    case 'MOVE':
      return 'Move it back where it was';
    case 'MERGE':
      return 'Restore the page as it was before the merge';
  }
}
